import { Exception } from '@sentry/browser';
import { getFingerprintData } from '@thumbmarkjs/thumbmarkjs'
 
import { App } from './App';
 
import { getParameterByName, LogError } from '@utils/helpers';

export class Profiler{
    collectedData = {}; 
    constructor(){
        const isInDebug = getParameterByName("debug")
        if(!isInDebug) return;
        try{
            console.log("🪲 Debugging On")
            this.CollectData()
        }catch(ex: any){
            LogError(`Device Info Exception: ${ex.message}`)
        }
    }

    async CollectData(){
        let deviceInfo : any = {};

        // --- Native Browser API Collection ---
        const userAgent = navigator.userAgent;
        const browser = this.getBrowserInfo(userAgent);

        deviceInfo.versions = {
            sdk: process.env.sdkVersion,
            ui: process.env.VERSION
            }

        deviceInfo.browser = {
            userAgent: userAgent,
            name: browser.name,
            version: browser.version,
            //@ts-ignore
            language: navigator.language || navigator.userLanguage,
            languages: navigator.languages ? Array.from(navigator.languages) : [], // From snippet
            onlineStatus: navigator.onLine ? 'Online' : 'Offline',
            vendor: navigator.vendor || 'N/A',
        };

        deviceInfo.security = { // New security section
            cookieEnabled: navigator.cookieEnabled || 'N/A', // Moved from browser
            doNotTrack: navigator.doNotTrack === '1' ? 'Enabled' : (navigator.doNotTrack === '0' ? 'Disabled' : 'N/A') // Moved from browser
        };

        deviceInfo.os = {
            platform: navigator.platform,
            name: this.getOSName(userAgent, navigator.platform)
        };

        // Combine screen and window properties
        deviceInfo.screen = {
            width: window.screen.width,
            height: window.screen.height,
            colorDepth: window.screen.colorDepth,
            pixelDepth: window.screen.pixelDepth || 'N/A', // From snippet
            availWidth: window.screen.availWidth || 'N/A', // From snippet
            availHeight: window.screen.availHeight || 'N/A', // From snippet
            innerWidth: window.innerWidth, // From snippet
            innerHeight: window.innerHeight, // From snippet
            outerWidth: window.outerWidth, // From snippet
            outerHeight: window.outerHeight, // From snippet
            pixelRatio: window.devicePixelRatio,
            maxTouchPoints: navigator.maxTouchPoints || 0,
            isTouchscreen: false, // Will be updated by ThumbmarkJS if available
            mediaMatches: [] // Will be updated by ThumbmarkJS if available
        };

        deviceInfo.hardware = {
            //@ts-ignore
            deviceMemory: (navigator).deviceMemory || 'N/A', // From snippet
            hardwareConcurrency: navigator.hardwareConcurrency || 'N/A',
            touchSupport: { // From snippet
                maxTouchPoints: navigator.maxTouchPoints || 0,
                touchEvent: 'ontouchstart' in window,
                pointerEvent: typeof window.PointerEvent !== 'undefined',
            }
        };

        deviceInfo.gpu = this.getNativeWebGLInfo(); // Initial native WebGL info

        // const ipAddress = await this.fetchIpAddress();
        // const ipLocation = await this.fetchIpLocation(ipAddress);

        deviceInfo.network = {
            ipAddress: 'N/A',
            ipLocation:  'N/A', // Add IP location details
            type: 'N/A', // Renamed from connectionType
            effectiveType: 'N/A',
            rtt: 'N/A',
            downlink: 'N/A',
            saveData: 'N/A'
        };
        //@ts-ignore
        const connection = navigator.connection as any
        if (connection) {
            deviceInfo.network.type = connection.type;
            deviceInfo.network.effectiveType = connection.effectiveType;
            deviceInfo.network.rtt = connection.rtt;
            deviceInfo.network.downlink = connection.downlink || 'N/A';
            deviceInfo.network.saveData = connection.saveData || 'N/A';
        }

    
        deviceInfo.battery = {};
        if ('getBattery' in navigator) {
            try {
                //@ts-ignore
                const battery = await navigator.getBattery();
                deviceInfo.battery.charging = battery.charging;
                deviceInfo.battery.level = `${(battery.level * 100).toFixed(2)}%`;
                deviceInfo.battery.chargingTime = battery.chargingTime === Infinity ? 'N/A' : `${battery.chargingTime} seconds`;
                deviceInfo.battery.dischargingTime = battery.dischargingTime === Infinity ? 'N/A' : `${battery.dischargingTime} seconds`;
            } catch (e) {
                deviceInfo.battery.status = 'Permission Denied / Error';
                console.error("Battery API Error:", e);
            }
        } else {
            deviceInfo.battery.status = 'N/A (API not supported)';
        }

        

        deviceInfo.userAgentClientHints = {};
        //@ts-ignore
        const userAgentData =  navigator.userAgentData as any
        if (userAgentData) {
            deviceInfo.userAgentClientHints.brands = userAgentData.brands;
            deviceInfo.userAgentClientHints.mobile = userAgentData.mobile;
            try {
                const highEntropyValues = await userAgentData.getHighEntropyValues(['architecture', 'model', 'platformVersion', 'uaFullVersion', 'bitness', 'formFactor', 'fullVersionList', 'platform', 'wow64']);
                deviceInfo.userAgentClientHints.architecture = highEntropyValues.architecture || 'N/A';
                deviceInfo.userAgentClientHints.model = highEntropyValues.model || 'N/A';
                deviceInfo.userAgentClientHints.platformVersion = highEntropyValues.platformVersion || 'N/A';
                deviceInfo.userAgentClientHints.uaFullVersion = highEntropyValues.uaFullVersion || 'N/A';
                deviceInfo.userAgentClientHints.bitness = highEntropyValues.bitness || 'N/A';
                deviceInfo.userAgentClientHints.formFactor = highEntropyValues.formFactor || 'N/A';
                deviceInfo.userAgentClientHints.fullVersionList = highEntropyValues.fullVersionList || 'N/A';
                deviceInfo.userAgentClientHints.platform = highEntropyValues.platform || deviceInfo.os.name;
                deviceInfo.userAgentClientHints.wow64 = highEntropyValues.wow64;
            } catch (e) {
                deviceInfo.userAgentClientHints.highEntropyStatus = 'Error collecting high-entropy values (e.g., permission denied, not available in current context)';
                console.error("User Agent Client Hints High Entropy Error:", e);
            }
        } else {
            deviceInfo.userAgentClientHints.status = 'N/A (API not supported)';
        }

        deviceInfo.capabilities = {
            webAudio: typeof AudioContext !== 'undefined',
            webRTC: typeof RTCPeerConnection !== 'undefined',
            webSockets: typeof WebSocket !== 'undefined',
            webAssembly: typeof WebAssembly !== 'undefined',
            webWorkers: typeof Worker !== 'undefined',
            sharedWorkers: typeof SharedWorker !== 'undefined',
            serviceWorkers: typeof navigator.serviceWorker !== 'undefined',
            canvas: (() => {
                try {
                    const canvasTest = document.createElement('canvas');
                    return !!(canvasTest.getContext && canvasTest.getContext('2d'));
                } catch (e) { return false; }
            })(),
            indexedDB: typeof indexedDB !== 'undefined',
            fetchAPI: typeof fetch !== 'undefined',
            performance: typeof performance !== 'undefined',
            webXR: typeof (navigator as any).xr !== 'undefined',
            gamepad: typeof navigator.getGamepads !== 'undefined',
            mediaDevices: typeof navigator.mediaDevices !== 'undefined'
        };

        deviceInfo.storage = this.checkStorageSupport(); // Renamed key to 'storage'

        deviceInfo.plugins = [];
        if (navigator.plugins && navigator.plugins.length > 0) {
            for (let i = 0; i < navigator.plugins.length; i++) {
                deviceInfo.plugins.push(navigator.plugins[i].name);
            }
        }
        ///@ts-ignore
        const userLanguage = navigator.userLanguage
        deviceInfo.locales = { // New locales section
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, // From snippet
            browserLanguage: navigator.language || userLanguage, // Already collected under browser, but for explicit locale
            browserLanguages: navigator.languages ? Array.from(navigator.languages) : [],
        };

        deviceInfo.timestamps = { // New timestamps section
            localTime: new Date().toString() // From snippet
        };

        deviceInfo.locationInfo = { // New location info section
            href: window.location.href // From snippet
        };

        // --- ThumbmarkJS Collection ---
       
        try {
 
            const thumbmarkData = await getFingerprintData();

            // Use ThumbmarkJS data to enhance existing properties or add specific details
            const system = thumbmarkData.system as any
            if (system) {
                // Prioritize browser info from ThumbmarkJS if more granular
                deviceInfo.browser.name = system.browser.name || deviceInfo.browser.name;
                deviceInfo.browser.version = system.browser.version || deviceInfo.browser.version;
                deviceInfo.os.platform = system.platform || deviceInfo.os.platform;
                deviceInfo.hardware.hardwareConcurrency = system.hardwareConcurrency || deviceInfo.hardware.hardwareConcurrency;
                deviceInfo.security.cookieEnabled = system.cookieEnabled;

                // Add other system properties
                deviceInfo.systemDetails = {
                    product: system.product || 'N/A',
                    productSub: system.productSub || 'N/A',
                    applePayVersion: system.applePayVersion || 0
                };
            }

            const screen = thumbmarkData.screen as any
            if (screen) {
                deviceInfo.screen.isTouchscreen = screen.is_touchscreen;
                deviceInfo.screen.maxTouchPoints = screen.maxTouchPoints;
                deviceInfo.screen.colorDepth = screen.colorDepth;
                deviceInfo.screen.mediaMatches = screen.mediaMatches;
                // ThumbmarkJS also provides resolution, but window.screen.width/height are often more direct for primary display
            }
            const hardware = thumbmarkData.hardware as any
            if (hardware && hardware.videocard) {
                deviceInfo.gpu = { // Overwrite or enhance native GPU info
                    vendor: hardware.videocard.vendor,
                    renderer: hardware.videocard.renderer,
                    version: hardware.videocard.version,
                    shadingLanguageVersion: hardware.videocard.shadingLanguageVersion
                };
                deviceInfo.hardware.architecture = hardware.architecture || 'N/A';
                deviceInfo.hardware.deviceMemory = hardware.deviceMemory || 'N/A'; // Consolidate deviceMemory
                deviceInfo.hardware.jsHeapSizeLimit = hardware.jsHeapSizeLimit || 'N/A';
            }
            // Remove nativeWebgl property after consolidation
            if (deviceInfo.nativeWebgl) {
                    delete deviceInfo.nativeWebgl;
            }

            const locales = thumbmarkData.locales as any
            if (locales) {
                deviceInfo.locales.timezone = locales.timezone || deviceInfo.locales.timezone;
                deviceInfo.locales.languages = locales.languages ? locales.languages.split(',') : deviceInfo.locales.languages; // ThumbmarkJS returns comma-separated string
            }

            if (thumbmarkData.permissions) {
                // Merging permissions from ThumbmarkJS into a top-level permissions object
                deviceInfo.permissions = thumbmarkData.permissions;
            }

            if (thumbmarkData.audio) {
                deviceInfo.audio = thumbmarkData.audio;
            }

            const canvas = thumbmarkData.canvas as any
            if (canvas) {
                deviceInfo.canvasFingerprint = canvas.commonImageDataHash;
            }

            if (thumbmarkData.fonts) {
                deviceInfo.fonts = {
                    availableFonts: Object.keys(thumbmarkData.fonts),
                    metrics: thumbmarkData.fonts
                };
            }

            if (thumbmarkData.math) {
                deviceInfo.mathFingerprint = thumbmarkData.math;
            }
            const webgl = thumbmarkData.webgl as any
            if (webgl) {
                deviceInfo.webglFingerprint = webgl.commonImageHash;
            }

            
        } catch (ex : any) {
            deviceInfo.thumbmarkStatus = `Error collecting with ThumbmarkJS: ${ex.message}`;
            console.error("Error collecting with ThumbmarkJS:", ex);
        }

        // Final Display
        this.collectedData = deviceInfo; // Store the full data
        App.classicCrash.UILogs(this.collectedData)
    
    }

    // Function to fetch IP address
    // async fetchIpAddress() {
    //     try {
    //         const response = await fetch('https://api.ipify.org?format=json');
    //         const data = await response.json();
    //         return data.ip;
    //     } catch (error) {
    //         console.error("Error fetching IP address:", error);
    //         return 'N/A';
    //     }
    // }

    // Function to fetch IP location details
    // async fetchIpLocation(ip: string) {
    //     if (ip === 'N/A') return null;
    //     try {
    //         // Using ipapi.co for more detailed location data
    //         const response = await fetch(`https://ipapi.co/${ip}/json/`);
    //         const data = await response.json();
    //         if (data && !data.error) {
    //             return {
    //                 city: data.city || 'N/A',
    //                 region: data.region || 'N/A',
    //                 country: data.country_name || 'N/A',
    //                 countryCode: data.country_code || 'N/A',
    //                 latitude: data.latitude || 'N/A',
    //                 longitude: data.longitude || 'N/A',
    //                 organization: data.org || 'N/A',
    //                 asn: data.asn || 'N/A',
    //                 timezone: data.timezone || 'N/A'
    //             };
    //         } else {
    //             console.error("Error fetching IP location details:", data.reason || "Unknown error");
    //             return null;
    //         }
    //     } catch (error) {
    //         console.error("Error fetching IP location:", error);
    //         return null;
    //     }
    // }


    // Function to get browser name and version from User Agent
    getBrowserInfo(userAgent: string) {
        let browserName = "Unknown";
        let browserVersion = "Unknown";

        if (userAgent.includes("Firefox")) {
            browserName = "Firefox";
            browserVersion = userAgent.match(/Firefox\/([0-9.]+)/)?.[1] || "Unknown";
        } else if (userAgent.includes("Opera") || userAgent.includes("Opr")) {
            browserName = "Opera";
            browserVersion = userAgent.match(/(Opera|Opr)\/([0-9.]+)/)?.[2] || "Unknown";
        } else if (userAgent.includes("Edge")) {
            browserName = "Edge";
            browserVersion = userAgent.match(/Edge\/([0-9.]+)/)?.[1] || "Unknown";
        } else if (userAgent.includes("Chrome")) {
            browserName = "Chrome";
            browserVersion = userAgent.match(/Chrome\/([0-9.]+)/)?.[1] || "Unknown";
        } else if (userAgent.includes("Safari")) {
            browserName = "Safari";
            browserVersion = userAgent.match(/Version\/([0-9.]+).*Safari/)?.[1] || "Unknown";
        } else if (userAgent.includes("MSIE") || userAgent.includes("Trident")) {
            browserName = "Internet Explorer";
            browserVersion = userAgent.match(/(MSIE |rv:)([0-9.]+)/)?.[2] || "Unknown";
        }
        return { name: browserName, version: browserVersion };
    }

    // Function to get OS name (best effort)
    getOSName(userAgent: string, platform: string) {
        if (platform.startsWith("Win")) return "Windows";
        if (platform.startsWith("Mac")) return "macOS";
        if (platform.startsWith("Linux")) return "Linux";
        if (platform === "iPhone" || platform === "iPad" || platform === "iPod") return "iOS";
        if (userAgent.includes("Android")) return "Android";
        return "Unknown";
    }

    // Function to get WebGL Info from native API (as fallback/comparison)
    getNativeWebGLInfo() {
        try {
            const canvas = document.createElement('canvas');
            const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl') as WebGLRenderingContext;
            if (gl) {
                const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
                const renderer = debugInfo ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER);
                const vendor = debugInfo ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) : gl.getParameter(gl.VENDOR);
                return { renderer, vendor, version: gl.getParameter(gl.VERSION), shadingLanguageVersion: gl.getParameter(gl.SHADING_LANGUAGE_VERSION) };
            }
        } catch (e) {
            console.error("Native WebGL not supported or error:", e);
        }
        return null;
    }

    // Function to check for Storage API support
    checkStorageSupport() {
        const storageSupport = {
            localStorage: false,
            sessionStorage: false,
            indexedDB: false,
            quota: {
                temporary: 'N/A',
                usage: 'N/A'
            }
        };
        try {
            localStorage.setItem('test', 'test');
            localStorage.removeItem('test');
            storageSupport.localStorage = true;
        } catch (e) { /* Local Storage not supported or blocked */ }
        try {
            sessionStorage.setItem('test', 'test');
            sessionStorage.removeItem('test');
            storageSupport.sessionStorage = true;
        } catch (e) { /* Session Storage not supported or blocked */ }
        if (window.indexedDB) {
            storageSupport.indexedDB = true;
        }

        if (navigator.storage && navigator.storage.estimate) {
            navigator.storage.estimate().then(estimate => {
                storageSupport.quota.temporary = `${((estimate.quota ?? 0) / (1024 * 1024)).toFixed(2)} MB`;
                storageSupport.quota.usage = `${((estimate.usage ?? 0) / (1024 * 1024)).toFixed(2)} MB`;
            }).catch(e => {
                console.error("Storage Quota API Error:", e);
                storageSupport.quota.temporary = 'Error';
                storageSupport.quota.usage = 'Error';
            });
        }
        return storageSupport;
    }

    requestGeoLocation(){
        // deviceInfo.geolocation = { latitude: 'N/A', longitude: 'N/A', accuracy: 'N/A' };
        // const geolocationPromise = new Promise((resolve) => {
        //     if ("geolocation" in navigator) {
        //         statusMessage.textContent = 'Collecting information... (Waiting for Geolocation permission)';
        //         navigator.geolocation.getCurrentPosition(
        //             (position) => {
        //                 deviceInfo.geolocation.latitude = position.coords.latitude.toFixed(6);
        //                 deviceInfo.geolocation.longitude = position.coords.longitude.toFixed(6);
        //                 deviceInfo.geolocation.accuracy = position.coords.accuracy.toFixed(2) + ' meters';
        //                 resolve();
        //             },
        //             (error) => {
        //                 let errorMessage;
        //                 switch(error.code) {
        //                     case error.PERMISSION_DENIED:
        //                         errorMessage = "Permission Denied";
        //                         break;
        //                     case error.POSITION_UNAVAILABLE:
        //                         errorMessage = "Position Unavailable";
        //                         break;
        //                     case error.TIMEOUT:
        //                         errorMessage = "Request Timeout";
        //                         break;
        //                     default:
        //                         errorMessage = "An unknown error occurred";
        //                         break;
        //                 }
        //                 deviceInfo.geolocation.status = errorMessage;
        //                 console.error("Geolocation Error:", error);
        //                 resolve();
        //             },
        //             { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        //         );
        //     } else {
        //         deviceInfo.geolocation.status = 'N/A (API not supported)';
        //         resolve();
        //     }
        // });
        // await geolocationPromise;
    }

}